import { Grid, Divider, Typography, Box } from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import { callAPI, getLocalValue, LoacalVariables, Method } from "../../../commonFunctions/CommonFunctions";
import SlotAvailabilityCalendar from "./SlotAvailabilityCalender";
import WSTextField, { TextType } from "../../../Components/TextBox/WSTextBox";
import WSTimePicker from "../../../Components/WSTimePicker/WSTimePicker";
import WSSCheckBoxField from "../../../Components/CheckBox/WSCheckBox";
import dayjs from "dayjs";

interface SlotDrawerProps {
  id: string;
  onCancel: () => void;
  onSuccess?: () => void;
}

// New slots start as 10-minute, available slots
const DEFAULT_SLOT_VALUES = {
  durationInMinutes: "10",
  isAvailable: true,
  startTime: "",
  endTime: "",
  dates: [] as string[],
  date: "",
};

const SlotDrawer: React.FC<SlotDrawerProps> = ({ id, onCancel, onSuccess }) => {
  const { showMessage } = useSnackbar();
  const [labId, setLabId] = useState("");

  // Own form, so these fields don't overlap the page's Duration / Date filters
  const { control, reset, setValue } = useForm({ defaultValues: DEFAULT_SLOT_VALUES });

  const formValues = useWatch({ control });

  useEffect(() => {
    const userId = getLocalValue(LoacalVariables.UserId);
    setLabId(userId || "");
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    const duration = parseInt(formValues?.durationInMinutes || "0");
    const dates: string[] = Array.isArray(formValues?.dates) ? formValues.dates : [];
    if (dates.length === 0 || !formValues?.startTime || !formValues?.endTime || duration <= 0) {
      showMessage("Please fill ALL fields correctly", "error");
      return;
    }
    // "HH:mm" strings compare correctly as text
    if (formValues.endTime <= formValues.startTime) {
      showMessage("End time must be after start time", "error");
      return;
    }

    const slot = {
      durationInMinutes: duration,
      startTime: `${formValues.startTime}:00`,
      endTime: `${formValues.endTime}:00`,
      isAvailable: formValues.isAvailable !== false
    };

    // One request per date: when several days are sent in one request the API
    // only saves one of them, so each day is created on its own.
    const failures: { day: string; reason: string }[] = [];
    for (const day of dates) {
      try {
        const payload = [{ labId: labId.toUpperCase(), days: [{ day, slots: [slot] }] }];
        const response: any = await callAPI(`lab/LabSlot`, Method.POST, payload);
        if (response?.success === false) {
          failures.push({ day, reason: response?.message || "Rejected by server" });
        }
      } catch (error: any) {
        console.error("Slot create failed for", day, error.response?.data || error);
        const data = error.response?.data;
        failures.push({ day, reason: (typeof data === "string" ? data : data?.message) || "Failed to create slot" });
      }
    }

    const created = dates.length - failures.length;
    const label = (day: string) => dayjs(day).format("DD MMM");

    if (failures.length === 0) {
      showMessage(
        dates.length === 1 ? "✅ Slot created successfully!" : `✅ Slot created for all ${dates.length} days!`,
        "success"
      );
      reset();
      onSuccess?.();
      onCancel();
      return;
    }

    const failedText = failures.map((f) => `${label(f.day)} (${f.reason})`).join(", ");
    if (created > 0) {
      // Keep the drawer open so the failed days can be retried
      showMessage(`Slot created for ${created} of ${dates.length} days. Not created: ${failedText}`, "warning");
      onSuccess?.();
    } else {
      showMessage(`Slot not created. ${failedText}`, "error");
    }
  }, [formValues, labId, reset, onSuccess, onCancel, showMessage]);

  return (
    <form id={id} onSubmit={handleSubmit} style={{ height: "100%" }}>
      <Grid container spacing={2} sx={{ p: 2, height: "100%" }}>
        {/* 1. DURATION */}
        <Grid item xs={12}>
          <WSTextField
            control={control}
            name="durationInMinutes"
            id="durationInMinutes"
            rules={{ 
              required: "Duration required",
              pattern: { value: /^[0-9]+$/, message: "Enter number" }
            }}
            label="Duration (minutes)*"
            type={TextType.Number}
          />
        </Grid>

        {/* 2. DATE PICKER */}
        <Grid item xs={12}>
          <SlotAvailabilityCalendar
            onDatesChange={(dates) => {
              setValue("dates", dates, { shouldDirty: true });
              // First date kept in "date" for anything that still reads it
              setValue("date", dates[0], { shouldDirty: true });
            }}
          />
        </Grid>

        {/* 3. TIME PICKERS */}
        <Grid item xs={12} sm={6}>
          <WSTimePicker
            control={control}
            name="startTime"
            id="startTime"
            label="Start Time*"
            rules={{ required: "Start time required" }}
            timeSize="small"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <WSTimePicker
            control={control}
            name="endTime"
            id="endTime"
            label="End Time*"
            rules={{ required: "End time required" }}
            timeSize="small"
          />
        </Grid>

        {/* 4. AVAILABILITY - 🔥 FIXED: Removed rules prop */}
        <Grid item xs={12}>
          <WSSCheckBoxField
  control={control}
  name="isAvailable"
  id="isAvailable"
  label="Mark as Available"
  switchType="IOS"
  rules={{ required: "Availability required" }}
/>

        </Grid>

        {/* 5. LAB ID DISPLAY */}
        <Grid item xs={12}>
          <Divider sx={{ my: 3 }} />
          <Box sx={{ opacity: 0.7 }}>
            <Typography variant="caption">
              Lab ID: <strong>{labId || "Loading..."}</strong>
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </form>
  );
};

export default SlotDrawer;

